/** @jsxImportSource @emotion/react */

import { CollaboratorManageModal } from "components/Collaborators/CollaboratorManageModal";
import { useGlobalModals } from "components/GlobalModals";
import { missionReadRoles } from "configs/role";
import { getMissionSpecificRoles } from "helpers/access";
import {
  CSSRulesResolver,
  useCSSRulesWithTheme,
} from "hooks/useCSSRulesWithTheme";
import { useAccountCollaborators } from "queries/accounts/collaborators/useAccountCollaborators";
import { FC, useCallback, useEffect, useMemo } from "react";
import { useRootStore } from "store";
import { Avatar, ScreenClass } from "@a_team/ui-components";
import useAvatar from "hooks/avatars/useAvatar";
import { css } from "@emotion/react";
import { globalStyleClasses } from "views/Mission/TeamSpecV2/globalStyles";
import { useMission } from "views/Mission/TeamSpecV2/MissionContext";
import { useLocation } from "react-router-dom-v5-compat";
import { useHistory } from "react-router-dom";
import { MANAGE_COLLABORATORS_PARAM } from "components/Collaborators/constants";
import ActionButton from "views/Mission/TeamSpecV2/components/ActionButton";

const cssRules: CSSRulesResolver<{ isMobile: boolean }> = ({ colors }) => ({
  container: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  button: {
    height: 34,
    borderRadius: 12,
    border: "2px solid white",
  },
  avatarList: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap" as const,
  },
  avatar: {
    objectFit: "cover",
    width: 30,
    height: 28,
    border: "2px solid white",
    marginRight: -5,
  },
  showCount: {
    backgroundColor: colors.Grey[200],
    color: colors.Grey[900],
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "37.5%",
    fontWeight: 500,
    fontSize: 14,
  },
});

export const CollaboratorAvatar: FC<{
  profilePictureURL: string;
  size?: ScreenClass;
  className?: string;
}> = ({ profilePictureURL, size = "sm", className }) => {
  const styles = useCSSRulesWithTheme(cssRules);
  const avatar = useAvatar(profilePictureURL);
  return <Avatar size={size} src={avatar} css={className || styles.avatar} />;
};

export const ShowCount: FC<{
  count?: number;
  className?: string;
  size?: number;
}> = ({ count, className, size = 28 }) => {
  const styles = useCSSRulesWithTheme(cssRules);
  if (typeof count !== "number") {
    return null;
  }
  return (
    <div
      css={css(styles.avatar, styles.showCount, className)}
      style={{
        width: size,
        height: size,
      }}
    >
      +{count}
    </div>
  );
};

const CollaboratorsButton: FC = () => {
  const {
    accountsStore: { currentAccountId },
  } = useRootStore();
  const globalStyles = useCSSRulesWithTheme(globalStyleClasses);
  const styles = useCSSRulesWithTheme(cssRules);
  const { mission } = useMission();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const history = useHistory();

  const { data: collaborators } = useAccountCollaborators(currentAccountId);
  const { addModal, removeModal } = useGlobalModals();

  const manageModal = useCallback(
    () => (
      <CollaboratorManageModal isOpen close={() => removeModal(manageModal)} />
    ),
    [removeModal]
  );

  const filteredCollaborators = useMemo(() => {
    if (!collaborators) {
      return [];
    }
    return collaborators.filter((collaborator) => {
      const roles = getMissionSpecificRoles(
        collaborator.roles || [],
        mission?._id
      );
      const imageUrl =
        collaborator.user.pictureURL || collaborator.user.profilePictureURL;
      return imageUrl && roles.some((role) => missionReadRoles.includes(role));
    });
  }, [collaborators, mission?._id]);

  useEffect(() => {
    if (params.has(MANAGE_COLLABORATORS_PARAM)) {
      addModal(manageModal);
      params.delete(MANAGE_COLLABORATORS_PARAM);
      history.replace({ search: params.toString() });
    }
  }, [location, addModal, manageModal, history]);

  return (
    <>
      <div css={styles.container}>
        <div
          css={css(styles.avatarList, globalStyles.mobileHide)}
          data-testing-id="collaborator-tags"
        >
          {filteredCollaborators.slice(0, 5)?.map(({ user }) => (
            <CollaboratorAvatar
              key={user.id}
              profilePictureURL={user.profilePictureURL}
            />
          ))}
        </div>
        <ActionButton
          data-testing-id="invitation-modal"
          name="Invite collaborator"
          onClick={() => addModal(manageModal)}
          size={"sm"}
          variant="secondary"
          css={styles.button}
          txt="Add collaborators"
        />
      </div>
    </>
  );
};

export default CollaboratorsButton;
